import type { EuiGlobalToastListProps, IconType } from '@elastic/eui';
import {
  EuiButtonEmpty,
  EuiButtonIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiGlobalToastList,
  EuiHeaderBreadcrumbs,
  EuiHorizontalRule,
  EuiLink,
  EuiPage,
  EuiPageBody,
  EuiPageHeader,
  EuiPageSection,
  EuiProvider,
  EuiSpacer,
  EuiText,
  useEuiTheme,
} from '@elastic/eui';
import { css } from '@emotion/react';
import axios from 'axios';
import type { MouseEvent, ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { useLocalStorage } from '../hooks';
import type { Parameters, SerializedParameters, Settings } from '../model';
import { DEFAULT_DOMICILE_CODE, getDomicileLabel, getDomicileURL, settingsDefault } from '../model';
import { ContactFormModal } from './contact_form_modal';
import { DomicileSelectorModal } from './domicile_selector_modal';
import type { PageContextValue } from './page_context';
import { PageContext } from './page_context';
import { SettingsFlyout } from './settings_flyout';
import { PageErrorState } from '../components';

export interface PageContainerProps {
  domicile?: string;
}

export interface PageToast {
  id: string;
  title?: ReactNode;
  text?: ReactElement;
  iconType?: IconType;
  color?: 'primary' | 'success' | 'warning' | 'danger';
}

type Toast = Required<EuiGlobalToastListProps>['toasts'][0];

export function PageContainer({ domicile }: PageContainerProps) {
  const [settings, setSettings] = useLocalStorage<Settings>('settings', settingsDefault());
  const [toasts, setToasts] = useState<PageToast[]>([]);
  const removeToast = useCallback(
    (removedToast: Toast) => {
      setToasts(toasts.filter((toast) => toast.id !== removedToast.id));
    },
    [toasts],
  );

  const euiTheme = useEuiTheme();

  const navigate = useNavigate();
  const location = useLocation();
  const domicileCode = domicile ?? settings.country ?? DEFAULT_DOMICILE_CODE;

  useEffect(() => {
    // If page is loaded without domicile in URL, but settings are configured for non-default domicile, we should update
    // current URL.
    if (settings.country !== DEFAULT_DOMICILE_CODE && !domicile) {
      navigate(
        { pathname: `/${settings.country}${location.pathname}`, hash: location.hash, search: location.search },
        { replace: true },
      );
    }
  });

  const [parameters, setParameters] = useState<Parameters>({
    synced: false,
    status: { level: 'available' },
    domicileCode,
    domiciles: [],
    providers: new Map(),
    fundAssetClasses: new Map(),
    assetClasses: new Map(),
    notices: [],
    license: {
      maxFundsToAnalyze: Infinity,
      maxAssetsToAnalyze: Infinity,
    },
  });
  useEffect(() => {
    // Don't reload parameters if domicile hasn't changed.
    if (parameters.synced && domicileCode === parameters.domicileCode) {
      return;
    }

    axios.get(`/api/parameters/${encodeURIComponent(domicileCode)}`).then(
      ({ data }: { data: SerializedParameters }) => {
        setParameters({
          synced: true,
          status: data.status,
          domicileCode: data.domicileCode,
          domiciles: data.domiciles,
          providers: new Map(data.providers.map((providerType) => [providerType.type, providerType])),
          fundAssetClasses: new Map(
            data.fundAssetClasses.map((assetClassType) => [assetClassType.type, assetClassType]),
          ),
          assetClasses: new Map(data.assetClasses.map((assetClassType) => [assetClassType.type, assetClassType])),
          notices: data.notices,
          license: data.license,
        });
      },
      () => {
        setParameters({ ...parameters, status: { level: 'unavailable' }, synced: true });
      },
    );
  }, [domicileCode, settings, parameters]);

  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);
  const onToggleSettings = useCallback(() => {
    setIsSettingsOpen(!isSettingsOpen);
  }, [isSettingsOpen]);

  const [isDomicileSelectorOpen, setIsDomicileSelectorOpen] = useState<boolean>(false);
  const onToggleDomicileSelector = useCallback(() => {
    setIsDomicileSelectorOpen(!isDomicileSelectorOpen);
  }, [setIsDomicileSelectorOpen, isDomicileSelectorOpen]);

  const content =
    parameters?.status?.level === 'unavailable' ? (
      <PageErrorState
        title="Cannot connect to the server"
        content={
          <p>
            The <strong>ETFVue</strong> server is temporary not available.
          </p>
        }
      />
    ) : (
      <Outlet />
    );

  const settingsFlyout = isSettingsOpen ? <SettingsFlyout onClose={onToggleSettings} /> : null;
  const domicileSelectorModal = isDomicileSelectorOpen ? (
    <DomicileSelectorModal onClose={onToggleDomicileSelector} />
  ) : null;

  const [isContactFormOpen, setIsContactFormOpen] = useState<boolean>(false);
  const contactFormModal = isContactFormOpen ? (
    <ContactFormModal
      onClose={() => {
        setIsContactFormOpen(!isContactFormOpen);
      }}
    />
  ) : null;

  const pageTitle = useMemo<string | undefined>(() => {
    if (location.pathname.includes('/about-us')) {
      return 'About Us';
    }

    if (location.pathname.includes('/privacy')) {
      return 'Privacy Policy';
    }

    if (location.pathname.includes('/terms')) {
      return 'Terms of Use';
    }

    if (location.pathname.includes('/assets')) {
      return 'Assets';
    }

    if (location.pathname.includes('/analyze')) {
      return 'Analyze';
    }

    if (location.pathname.includes('/service')) {
      return 'Service';
    }

    if (location.pathname.includes('/portfolios')) {
      return 'Portfolios';
    }
  }, [location]);

  const context: PageContextValue = {
    settings,
    setSettings,
    parameters,
    domicileCode,
    addToast(toast: PageToast) {
      setToasts([...toasts, toast]);
    },
  };

  return (
    <EuiProvider colorMode={settings.theme}>
      <PageContext.Provider value={context}>
        <EuiPage
          css={css`
            min-height: 100vh;
          `}
          restrictWidth="1500px"
          paddingSize="m"
        >
          <EuiPageBody>
            <EuiPageHeader
              pageTitle={
                <EuiFlexGroup gutterSize="s">
                  <EuiFlexItem grow={false}>
                    <svg
                      style={{ margin: 'auto' }}
                      onClick={() => {
                        navigate(getDomicileURL(domicileCode, '/'));
                      }}
                      aria-labelledby="uniqueTitleID"
                      role="img"
                      width="140"
                      height="30"
                      version="1.1"
                      viewBox="0 0 140 30"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <title id="uniqueTitleID">{pageTitle ? `ETFVue - ${pageTitle}` : 'ETFVue'}</title>
                      <g aria-label="ETF Vue">
                        <path
                          fill="#d46969"
                          d="m1.9361 2.4401h4.1302v24.951h-4.1302zm14.557 13.711h-9.0391v-2.776h9.0391zm-9.0391 8.1589h10.089v3.0807h-10.089zm10.089-21.87v3.0807h-10.089v-3.0807zm23.156 0v2.776h-7.4479v22.174h-4.0964v-22.174h-7.4479v-2.776zm4.9427 0h4.1302v24.951h-4.1302zm14.557 13.711h-9.0391v-2.776h9.0391zm1.0495-13.711v3.0807h-10.089v-3.0807z"
                        />
                        <path
                          fill="#6092c0"
                          d="m73.741 2.4401h4.638l5.2474 19.568v5.5182h-2.1328zm16.555 0h4.6042l-8.125 25.086h-1.7604v-6.6016zm13.474 16.25q0 1.1849 0.10156 2.2344 0.10156 1.0156 0.4401 1.7943 0.33855 0.77865 1.0156 1.2526 0.67708 0.47396 1.7943 0.47396 0.44011 0 1.2526-0.13542 0.8125-0.13542 1.5573-0.4401v2.5052q-0.88021 0.47396-1.9297 0.84635-1.0156 0.33854-2.0312 0.33854-0.8125 0-1.4557-0.16927-0.64323-0.13542-1.2188-0.4401-0.54167-0.33854-1.0495-0.74479-0.50781-0.40625-1.0156-0.91406-0.57552-0.57552-0.91406-1.3203-0.33854-0.77865-0.50781-1.5911-0.13542-0.84636-0.16927-1.6927 0-0.84636 0-1.6589v-10.732h4.1302zm11.68-10.393v19.094h-3.6901l-0.44011-1.9635v-17.13zm14.625-0.16927q1.8281 0.16927 3.2839 0.98177 1.4896 0.8125 2.5391 2.099 1.0495 1.2865 1.5911 2.9453 0.57553 1.6589 0.57553 3.4531 0 0.8125-0.10157 1.625h-11.612v-2.9792h7.8203q0-0.88021-0.30468-1.6927-0.27084-0.8125-0.84636-1.4896-0.54167-0.71094-1.2865-1.1849t-1.6589-0.57552zm-0.13541 16.081q0.37239 0 0.91406-0.06771 0.57552-0.10156 1.1849-0.30469 0.64323-0.23698 1.2526-0.57552 0.64323-0.33854 1.1172-0.8125l1.4896 2.2682q-1.1172 1.388-2.6068 2.099-1.4896 0.71094-3.7917 0.71094-1.862 0-3.487-0.74479-1.625-0.77865-2.776-2.099-1.151-1.3203-1.8281-3.0807-0.67708-1.7604-0.67708-3.7917 0-1.9297 0.60937-3.6563 0.64323-1.7266 1.7266-3.013 1.0833-1.3203 2.5052-2.1328 1.4557-0.8125 3.1146-0.91406v3.25q-1.2526 0.23698-2.0651 1.0156-0.8125 0.77865-1.2865 1.7943-0.44011 0.98177-0.64323 2.0313-0.16927 1.0495-0.16927 1.7943 0 1.3203 0.30468 2.4714 0.33855 1.1172 1.0156 1.9635 0.67708 0.8125 1.6589 1.3203 1.0156 0.47396 2.4375 0.47396z"
                        />
                      </g>
                    </svg>
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiHeaderBreadcrumbs
                      aria-label="Navigation menu"
                      breadcrumbs={
                        pageTitle
                          ? [
                              {
                                text: 'Home',
                                onClick: (e) => {
                                  e.preventDefault();
                                  navigate(getDomicileURL(domicileCode, '/'));
                                },
                                href: getDomicileURL(domicileCode, '/'),
                              },
                              { text: pageTitle },
                            ]
                          : [{ text: 'Home' }]
                      }
                      responsive={false}
                    />
                  </EuiFlexItem>
                </EuiFlexGroup>
              }
              paddingSize={'none'}
              alignItems={'center'}
              rightSideGroupProps={{ gutterSize: 'xs', justifyContent: 'center' }}
              rightSideItems={[
                <EuiButtonIcon
                  key="settings"
                  aria-label={'Settings'}
                  size={'m'}
                  display={'empty'}
                  color={'text'}
                  iconType="gear"
                  title={'Change your settings'}
                  onClick={onToggleSettings}
                />,
                <EuiButtonIcon
                  key="assets"
                  aria-label={'Stocks, Bonds, and Other Securities'}
                  size={'m'}
                  display={'empty'}
                  color={'text'}
                  iconType="visTable"
                  title={'Explore stocks, bonds, and other securities'}
                  href={getDomicileURL(domicileCode, '/assets')}
                  onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    navigate(getDomicileURL(domicileCode, '/assets'));
                  }}
                />,
                <EuiButtonIcon
                  key="portfolios"
                  aria-label={'Portfolios'}
                  size={'m'}
                  display={'empty'}
                  color={'text'}
                  iconType="submodule"
                  href={getDomicileURL(domicileCode, '/portfolios')}
                  onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    navigate(getDomicileURL(domicileCode, '/portfolios'));
                  }}
                  title={'Portfolios'}
                />,
                <EuiButtonEmpty
                  key="domicile"
                  aria-label={'Domicile'}
                  iconType="mapMarker"
                  color="primary"
                  onClick={onToggleDomicileSelector}
                  title={'Set your domicile'}
                >
                  {getDomicileLabel(parameters)}
                </EuiButtonEmpty>,
              ]}
            />
            <EuiPageSection
              grow
              color="plain"
              css={css`
                margin-top: ${euiTheme.euiTheme.size.base};
                border: ${euiTheme.euiTheme.border.width.thin} solid ${euiTheme.euiTheme.colors.lightShade};
                border-radius: ${euiTheme.euiTheme.border.radius.medium};
              `}
              contentProps={{
                css: css`
                  flex: 1;
                `,
              }}
            >
              <EuiFlexGroup style={{ height: '100%' }} direction={'column'} gutterSize={'none'}>
                <EuiFlexItem>{content}</EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiHorizontalRule size={'half'} />
                  <EuiText textAlign={'center'} size={'s'}>
                    <EuiLink
                      href={getDomicileURL(domicileCode, '/about-us')}
                      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                        e.preventDefault();
                        navigate(getDomicileURL(domicileCode, '/about-us'));
                      }}
                      color={'success'}
                    >
                      About Us
                    </EuiLink>{' '}
                    |{' '}
                    <EuiLink
                      href={getDomicileURL(domicileCode, '/privacy')}
                      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                        e.preventDefault();
                        navigate(getDomicileURL(domicileCode, '/privacy'));
                      }}
                      color={'success'}
                    >
                      Privacy Policy
                    </EuiLink>{' '}
                    |{' '}
                    <EuiLink
                      href={getDomicileURL(domicileCode, '/terms')}
                      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                        e.preventDefault();
                        navigate(getDomicileURL(domicileCode, '/terms'));
                      }}
                      color={'success'}
                    >
                      Terms of Use
                    </EuiLink>{' '}
                    |{' '}
                    <EuiLink
                      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                        e.preventDefault();
                        setIsContactFormOpen(!isContactFormOpen);
                      }}
                      color={'success'}
                    >
                      Contact Us
                    </EuiLink>
                  </EuiText>
                  <EuiSpacer size={'s'} />
                  <EuiText size={'xs'} color={'subdued'} textAlign={'center'}>
                    This page is not an investment advice and does not constitute any offer or solicitation to offer or
                    recommendation of any any investment product. There is no warranty for completeness, accuracy and
                    correctness for the displayed information. The basic stock information is sourced from{' '}
                    <EuiLink href={getDomicileURL(domicileCode, '/terms#stocks-data')} target={'_blank'}>
                      Investing.com
                    </EuiLink>
                    . ETF details and holdings are sourced directly from the{' '}
                    <EuiLink href={getDomicileURL(domicileCode, '/terms#providers-data')} target={'_blank'}>
                      respective fund providers
                    </EuiLink>
                    .
                  </EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
              <EuiGlobalToastList toasts={toasts} dismissToast={removeToast} toastLifeTimeMs={5000} />
            </EuiPageSection>
          </EuiPageBody>
          {settingsFlyout}
          {domicileSelectorModal}
          {contactFormModal}
        </EuiPage>
      </PageContext.Provider>
    </EuiProvider>
  );
}
